import { Box, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl, downloadFileFromUrlViaAxios } from "@entrilia/shared/services/downloadFile";
import { getRowsByIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import biApi from "../../../../api/biApi";
import type { WorkbookRun, WorkbookRunsProgress } from "../../../../api/types/workbookRuns.types";
import useClientContext from "../../../../context/useClientContext";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../common/filters/filterState";
import WorkbookCreatorFilter from "../common/WorkbookCreatorFilter";
import DeleteRunsDialog from "../dialogs/delete-runs/DeleteRunsDialog";
import useWorkbookCreatorCounts from "../hooks/useWorkbookCreatorCounts";
import useWorkbookRunsPolling from "../hooks/useWorkbookRunsPolling";
import WorkbookRunsGrid from "./WorkbookRunsGrid";
import { filterDefinitions, getWorkbookRunsFilterPayload } from "./workbookRunsGridFilters";
import { workbookRunsSelectOptionsResolver } from "./workbookRunsSelectOptionsResolver";
import { getJustFinishedRunIds, setRunsProgress } from "./workbookRunUtils";

const getRun = withErrorHandling(biApi.getWorkbookRun);
const cancelRun = withErrorHandling(biApi.cancelWorkbookRuns);
const downloadRun = withErrorHandling(biApi.getWorkbookRunDownloadInfo);
const downloadWorkbookRuns = withErrorHandling(biApi.downloadWorkbookRuns);

const RUN_FETCH_ATTEMPTS = 3;
const RUN_FETCH_RETRY_BASE_DELAY_MS = 2000;

const getRunWithRetries = async (runId: string): Promise<WorkbookRun | undefined> => {
  for (let attempt = 1; attempt <= RUN_FETCH_ATTEMPTS; attempt++) {
    const [run, error] = await getRun(runId);
    if (run) {
      return run;
    }
    logError(error, "[WorkbookRunsContainer] getRunWithRetries");
    if (attempt < RUN_FETCH_ATTEMPTS) {
      await wait(RUN_FETCH_RETRY_BASE_DELAY_MS * attempt);
    }
  }
  return undefined;
};

const WorkbookRunsContainer = () => {
  const { clientCode } = useClientContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { sendNotificationError } = useNotificationContext();
  const [downloadingIds, setDownloadingIds] = useState<string[]>([]);
  const [cancelingIds, setCancelingIds] = useState<string[]>([]);
  const [runsToDelete, setRunsToDelete] = useState<WorkbookRun[]>();
  const [createdByMe, setCreatedByMe] = useState(false);

  const showToolbar = Boolean(selectedIds.length);
  const filterId = useMemo(() => `${clientCode}_workbook_runs_filter_v2`, [clientCode]);
  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState<WorkbookRun>(filterId, filterDefinitions, [])
  );
  const filterPayload = useMemo(() => getWorkbookRunsFilterPayload(filterState), [filterState]);
  const gridPayload = useMemo(() => ({ ...filterPayload, createdByMe }), [filterPayload, createdByMe]);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource, reloadDataSource } =
    useGridDataSourceWithRange(biApi.searchWorkbookRuns, gridPayload);
  const { counts, refreshCounts } = useWorkbookCreatorCounts(biApi.searchWorkbookRuns, filterPayload);

  const refreshFinishedRuns = useCallback(
    async (runIds: string[]) => {
      const results = await Promise.all(runIds.map(getRunWithRetries));
      const runs = results.flatMap((run) => (run && gridApiRef.current?.getRow(run.id) ? [run] : []));
      if (runs.length > 0) {
        gridApiRef.current?.updateRows(runs);
      }
    },
    [gridApiRef]
  );

  const onDataLoaded = useCallback(
    (data: WorkbookRunsProgress) => {
      const rows = getRowsByIds<WorkbookRun>(
        gridApiRef,
        data.result.map((progress) => progress.runId)
      );
      gridApiRef.current?.updateRows(setRunsProgress(rows, data));
      void refreshFinishedRuns(getJustFinishedRunIds(rows, data));
    },
    [gridApiRef, refreshFinishedRuns]
  );

  useWorkbookRunsPolling(gridApiRef, onDataLoaded);

  const getRunPath = useCallback((id: string) => `run/${id}`, []);

  const handleCancelRun = useCallback(
    async (workbookRun: WorkbookRun) => {
      setCancelingIds((prev) => [...prev, workbookRun.id]);
      const [result, error] = await cancelRun({ runs: { [workbookRun.id]: workbookRun.operationId ?? "" } });
      const canceledRun = result?.results[workbookRun.id]?.workbookRun;
      if (error || !canceledRun) {
        logError(error, "[WorkbookRunsContainer] handleCancelRun");
        sendNotificationError("Could not cancel the run");
        setCancelingIds((prev) => prev.filter((id) => id !== workbookRun.id));
        return;
      }
      gridApiRef.current?.updateRows([canceledRun]);

      if (!result.results[workbookRun.id]?.isCanceled) {
        sendNotificationError("Failed to cancel the run");
      }
      setCancelingIds((prev) => prev.filter((id) => id !== workbookRun.id));
    },
    [sendNotificationError, gridApiRef]
  );

  const handleCancelRuns = useCallback(
    async (ids: string[]) => {
      const selectedRuns = getRowsByIds<WorkbookRun>(gridApiRef, ids);
      setCancelingIds((prev) => [...prev, ...selectedRuns.map((run) => run.id)]);
      const [result, error] = await cancelRun({
        runs: Object.fromEntries(selectedRuns.map((run) => [run.id, run.operationId ?? ""])),
      });
      const canceledRuns = selectedRuns
        .map((run) => result?.results[run.id]?.workbookRun)
        .filter((run) => run !== undefined);
      if (error || !canceledRuns) {
        logError(error, "[WorkbookRunsContainer] handleCancelRuns");
        sendNotificationError("Could not cancel some run");
        setCancelingIds((prev) => prev.filter((id) => !selectedRuns.some((run) => run.id === id)));
        return;
      }
      gridApiRef.current?.updateRows(canceledRuns);
      setCancelingIds((prev) => prev.filter((id) => !selectedRuns.some((run) => run.id === id)));
    },
    [sendNotificationError, gridApiRef]
  );

  const handleDownloadRun = useCallback(
    async (runId: string) => {
      setDownloadingIds((prev) => [...prev, runId]);
      const [downloadInfo, error] = await downloadRun(runId);
      if (error) {
        logError(error, "[WorkbookRunsContainer] handleDownloadRun");
        sendNotificationError("Could not download the run");
      } else {
        await downloadFileFromUrl(downloadInfo.downloadUrl);
      }
      setDownloadingIds((prev) => prev.filter((id) => id !== runId));
    },
    [sendNotificationError]
  );

  const handleDownloadRuns = useCallback(
    async (ids: string[]) => {
      const runsForDownloading = getRowsByIds<WorkbookRun>(gridApiRef, ids);
      setDownloadingIds((prev) => [...prev, ...runsForDownloading.map((run) => run.id)]);
      const [downloadUrl, error] = await downloadWorkbookRuns(
        { runIds: runsForDownloading.map((run) => run.id) },
        "workbook_runs.zip"
      );
      if (error || !downloadUrl) {
        logError(error, "[WorkbookRunsContainer] handleDownloadRuns");
        sendNotificationError("Could not download the runs");
      } else {
        await downloadFileFromUrlViaAxios(downloadUrl);
      }
      setDownloadingIds((prev) => prev.filter((id) => !runsForDownloading.some((run) => run.id === id)));
    },
    [sendNotificationError, gridApiRef]
  );

  const handleDeleteRuns = useCallback(
    (ids: string[]) => {
      setRunsToDelete(getRowsByIds<WorkbookRun>(gridApiRef, ids));
    },
    [gridApiRef]
  );

  const handleRefresh = () => {
    refreshDataSource();
    refreshCounts();
  };

  const handleRunsDeleted = (deletedIds: string[]) => {
    setRunsToDelete(undefined);
    setSelectedIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
    reloadDataSource();
    refreshCounts();
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Box>
        <WorkbookCreatorFilter createdByMe={createdByMe} counts={counts} onChange={setCreatedByMe} />
      </Box>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        getSelectOptionsForFilter={workbookRunsSelectOptionsResolver}
      >
        <FiltersPanel
          totalCount={totalRows}
          recordCount={totalRows ?? 0}
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          onRefresh={handleRefresh}
        />
        <WorkbookRunsGrid
          dataSource={dataSource}
          selectedIds={selectedIds}
          showToolbar={showToolbar}
          setSelectedIds={setSelectedIds}
          apiRef={gridApiRef}
          onDownloadRun={handleDownloadRun}
          onDownloadRuns={handleDownloadRuns}
          onCancelRuns={handleCancelRuns}
          onCancelRun={handleCancelRun}
          onDeleteRuns={handleDeleteRuns}
          getRunPath={getRunPath}
          cancelingIds={cancelingIds}
          downloadingIds={downloadingIds}
          filterId={filterId}
        />
      </FilterContextProvider>
      {runsToDelete && (
        <DeleteRunsDialog
          runs={runsToDelete}
          onClose={() => setRunsToDelete(undefined)}
          onDeleted={handleRunsDeleted}
        />
      )}
    </Stack>
  );
};

export default WorkbookRunsContainer;
